import type {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {computeWorldBoundsExcludingAnimatedDoorParts} from 'components/customer/RoomPlanner/helpers/calculations';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import {
    getSitOnFloorY,
    getWallTopY,
} from 'components/customer/RoomPlanner/helpers/placeholder/floorClearance';
import {clampGroupIntoRoomFootprint} from 'components/customer/RoomPlanner/helpers/wallProximity';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';

/**
 * Keep a resized placeholder inside the room. Width and depth grow from the
 * GLB pivot, so a size edit against a wall leaves the mesh through that wall.
 * Height keeps the current underside, then lifts it off the floor or drops it
 * under the ceiling when the new size no longer fits.
 * @param {CabinetMesh} group Placeholder mesh
 * @return {void}
 */
export const containPlaceholderInRoom = (group: CabinetMesh): void => {
    group.updateMatrixWorld(true);
    const box = computeWorldBoundsExcludingAnimatedDoorParts(group);
    if (
        !box.isEmpty() &&
        Number.isFinite(box.min.y) &&
        Number.isFinite(box.max.y)
    ) {
        const storedWallHeight = (group.userData as {wallHeight?: number})
            .wallHeight;
        const wallHeight =
            typeof storedWallHeight === 'number'
                ? storedWallHeight
                : getRoomDimensionsComputed().wallHeight;
        const floorY = getSitOnFloorY(wallHeight);
        const ceilingY = getWallTopY(wallHeight);
        const meshHeight = box.max.y - box.min.y;
        const maxBottom = ceilingY - meshHeight;
        const clampedBottom =
            maxBottom < floorY
                ? floorY
                : Math.max(floorY, Math.min(box.min.y, maxBottom));

        if (Math.abs(clampedBottom - box.min.y) > 1e-4) {
            group.position.y += clampedBottom - box.min.y;
            group.updateMatrixWorld(true);
        }
    }

    const parent = group.parent;
    if (!parent) return;

    const wallGroup =
        parent.getObjectByName(ObjectNameMap.rectangularWall) ??
        parent.getObjectByName(ObjectNameMap.lShapeWall);
    if (!wallGroup) return;

    clampGroupIntoRoomFootprint(group, wallGroup);
};
